import React, { useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ReactQuill from 'react-quill';
import { FormattedMessage, useIntl } from 'react-intl';
import CommonButton from '../../common/CommonButton';
import { Field, Form, Formik } from 'formik';
import Lottie from 'lottie-react';
import successAnimation from '../../../style/animations/success.json';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Box } from '@mui/material';
import {
  ALMOST_WHITE_COLOR,
  BACKGROUND_DARK,
  BLACK_COLOR, EXPAND_ROW_COLOR_DARK,
  VERY_LIGHT_GREY_COLOR,
  WHITE_COLOR,
} from '../../../const/color-const';
import FormTextField from '../../common/form/FormTextField';
import { darken } from '@material-ui/core/styles';
import { useValidator } from '../../../utils/validators';
import CommonSnackbar from '../../common/CommonSnackbar';
import { EditorFormats, EditorModules, editorStyles, editorThemeStyles } from '../freshdesk/NewTicket';
import emailjs from '@emailjs/browser';
import { formatMachinesListAsHTMLDiv } from './Utils';

const BORDER_COLOR_DARK = '#3d3d46';

const useStyles = makeStyles(theme => ({
  pageWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    alignSelf: 'center',
    width: '100%',
    borderRadius: '8px',
  },
  freshdeskHeader: {
    fontSize: '24px',
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  description: {
    textAlign: 'center',
    margin: theme.spacing(2),
  },
  formWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: theme.spacing(1),
    position: 'relative',
    maxWidth: 760,
    '& .ql-editor': {
      minHeight: 140,
    },
    '& div[class*="MuiTextField"]': {
      background: theme.palette.type === 'light' ? WHITE_COLOR : darken(BACKGROUND_DARK, 0.3),
      margin: theme.spacing(1, 0, 1, 0),
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      borderRadius: '10px',
      minWidth: 'unset',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(2, 1, 2, 1),
        maxWidth: '100%%',
      },
      '& > div': {
        borderRadius: '10px',
      },
      '& p.Mui-error': {
        visibility: 'hidden',
        position: 'absolute',
        bottom: -16,
      },
    },
  },
  quillWrapper: {
    ...editorStyles(theme),
    ...editorThemeStyles(theme),
    border: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
    marginTop: theme.spacing(0.5),
    borderRadius: 10,
    maxWidth: 750,
    width: '100%',
  },
  emailItem: {
    width: '100%',
    maxWidth: 740,
    display: 'flex',
    margin: theme.spacing(1, 4, 1, 2),
    justifyContent: 'flex-end',
  },
  fields: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& div[class*="MuiTextField"]': {
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(0),
        maxWidth: '49%',
      },
    },
  },
  submitButton: {
    width: 110,
    fontSize: 16,
    margin: theme.spacing(2),
  },
  btnWrapper: {
    margin: theme.spacing(2, 1, 2, 1),
  },
  navBtn: {
    width: 120,
    height: 40,
    margin: theme.spacing(2),
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    backgroundColor: theme.palette.type === 'light' ? VERY_LIGHT_GREY_COLOR : EXPAND_ROW_COLOR_DARK,
    '&:hover': {
      background: theme.palette.type === 'light' ? darken(VERY_LIGHT_GREY_COLOR, 0.03) : darken(EXPAND_ROW_COLOR_DARK, 0.2),
    },
  },
}));

const validator = ({ values, intl: { formatMessage } }) => {
  const errors = {};
  const fields = ['name', 'surname', 'subject'];
  
  fields.forEach(field => {
    if (!values[field]) {
      errors[field] = formatMessage({
        id: 'required',
        defaultMessage: 'Required',
      });
    }
  });
  
  return errors;
};

const ContactSalesForm = ({ user, machines }) => {
  const [isSnackbar, setIsSnackbar] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [content, setContent] = useState(null);
  const formValidator = useValidator(validator);
  const { EMail, Firstname, Lastname, UserName } = user;
  const { formatMessage } = useIntl();
  const classes = useStyles();
  
  const SERVICE_ID = process.env.REACT_APP_EMAIL_SALES_SERVICE_ID;
  const TEMPLATE_ID = process.env.REACT_APP_EMAIL_SALES_TEMPLATE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAIL_PUBLIC_KEY;
  
  const MachinesList = machines?.map(({ CustomerName, Name, Id }) => ({ CustomerName, Name, Id }));
  
  const stripHtml = html => {
    return html.replace(/<[^>]*>?/gm, '');
  };
  
  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const emailParams = {
      UserName: UserName,
      Firstname: values.name,
      Lastname: values.surname,
      Email: values.email,
      Subject: values.subject,
      MachinesList: formatMachinesListAsHTMLDiv(MachinesList),
      Description: stripHtml(content),
    };
    
    emailjs.send(SERVICE_ID, TEMPLATE_ID, emailParams, PUBLIC_KEY)
      .then(() => setIsSuccess(true))
      .catch(err => {
        setErrors({ submit: 'Failed to send message. Please try again later.' });
      })
      .finally(() => setSubmitting(false));
  };
  
  const successNotification = () => (
    <Box display={'flex'} flexDirection={'column'} justifyContent="center" alignItems="center">
      <Lottie loop={false} animationData={successAnimation} play
              style={{ width: 60, height: 60, marginBottom: 20, marginTop: 10 }} />
      <h2><FormattedMessage id="message.sent" defaultMessage="Ticket has been sent" /></h2>
      
      {isSnackbar && <CommonSnackbar
        open={isSnackbar}
        title={<FormattedMessage id="success" defaultMessage="Message" />}
        message={<FormattedMessage id="message.send" defaultMessage="Message send!" />}
        timeframe={6000}
        onExtraClose={() => setIsSnackbar(false)}
      />}
    </Box>
  );
  
  return (
    <div className={classes.pageWrapper}>
      <p className={classes.description}>
        <FormattedMessage id="contact.form.description1"
                          defaultMessage="Use this form to get in touch with our <b>sales deparetment</b>."
                          values={{ b: (chunks) => <strong>{chunks}</strong> }}
        /> <br />
  
        <FormattedMessage id="contact.form.description2"
                          defaultMessage="Fill in the details and we will get in touch with you as soon as we can!"
                          values={{ b: (chunks) => <strong>{chunks}</strong> }}
        /> <br />
      </p>
      
      {!isSuccess && <Formik
        enableReinitialize
        validate={formValidator}
        initialValues={{ email: EMail, name: Firstname, surname: Lastname }}
        onSubmit={handleSubmit}>
        {({ isSubmitting, isValid, dirty }) => (
          <Form className={classes.formWrapper}>
            <div className={classes.fields}>
              <Field
                name="name"
                component={FormTextField}
                label={(
                  <FormattedMessage
                    id="name"
                    defaultMessage="Name"
                  />
                )}
              />
              
              <Field
                name="surname"
                component={FormTextField}
                label={(
                  <FormattedMessage
                    id="surname"
                    defaultMessage="Surname"
                  />
                )}
              />
            </div>
            <Field
              name="subject"
              component={FormTextField}
              label={(
                <FormattedMessage
                  id="subject"
                  defaultMessage="Subject"
                />
              )}
            />
  
            <div className={classes.quillWrapper}>
              <ReactQuill
                placeholder={formatMessage({ id: 'description', defaultMessage: 'Description' })}
                theme="snow"
                value={content}
                onChange={setContent}
                formats={EditorFormats}
                modules={EditorModules}
              />
            </div>
            <span className={classes.emailItem}> <FormattedMessage id="freshdesk.email" defaultMessage="E-mail" />
              : {EMail}
            </span>
            <CommonButton
              className={classes.submitButton}
              disabled={isSubmitting || !isValid || !dirty || !content}
              isAnimated
              type="submit"
              size="large"
              label={<FormattedMessage id="send" defaultMessage="Submit" />}
            />
          </Form>
        )}
      </Formik>}
      
      {isSuccess && successNotification()}
    </div>
  );
};

ContactSalesForm.propTypes = {
  user: PropTypes.object,
  machines: PropTypes.array,
};

const decorate = compose(
  connect(store => ({
    user: store.user.profile,
    machines: store.user.machines,
  })),
);

export default decorate(ContactSalesForm);
